/**
 * Honeypot field for public lead forms.
 *
 * The ACL capture endpoint has scored a `_hp` field since 2026-08-21 — a filled
 * honeypot is worth 100 points on its own, enough to flag by itself. Nothing on
 * this site ever rendered the field, so that check has never once been able to
 * fire. This wires up the client half.
 *
 * Deliberately NOT `type="hidden"`: bots skip those. The field is a real, normal
 * text input pushed out of the viewport, so an automated form-filler treats it
 * like any other field and fills it in.
 *
 * Accessibility matters here — a screen reader must not offer this to a real
 * person and trip the trap:
 *   * aria-hidden keeps it out of the accessibility tree
 *   * tabIndex={-1} keeps it out of the tab order
 *   * autoComplete="off" stops the browser autofilling it for a human
 *   * the label is hidden the same way, so nothing is announced
 *
 * A human cannot see it, tab to it, or have it autofilled. Anything that
 * arrives with a value is automated.
 */

/** Off-screen rather than display:none — some bots skip undisplayed fields. */
const HIDDEN_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export function Honeypot({
  value,
  onChange,
  id = 'company_website',
}: {
  value: string;
  onChange: (v: string) => void;
  id?: string;
}) {
  return (
    <div style={HIDDEN_STYLE} aria-hidden="true">
      <label htmlFor={id}>Leave this field empty</label>
      <input
        type="text"
        id={id}
        name="_hp"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
      />
    </div>
  );
}
